import type { FC } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import ScrollEndFooter, { type ScrollEndFooterProps } from '../ScrollEndFooter';

interface OverflowAwareScrollEndFooterProps extends Omit<ScrollEndFooterProps, 'onBackToTop'> {
    onBackToTop?: () => void;
}

/**
 * Renders the shared end-of-scroll divider only when its nearest scrolling
 * ancestor contains more content than the viewport can display.
 */
const OverflowAwareScrollEndFooter: FC<OverflowAwareScrollEndFooterProps> = ({ onBackToTop, style, ...footerProps }) => {
    const boundaryRef = useRef<HTMLSpanElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const measurementFrameRef = useRef<number | undefined>(undefined);
    const [hasScrollableContent, setHasScrollableContent] = useState(false);
    const [inlinePadding, setInlinePadding] = useState({ end: 0, start: 0 });

    const resolveFooterContainer = useCallback(() => boundaryRef.current?.parentElement, []);
    const resolveScrollContainer = useCallback(() => {
        const footerContainer = resolveFooterContainer();

        if (!footerContainer || typeof window === 'undefined') {
            return footerContainer;
        }

        let ancestor: HTMLElement | null = footerContainer;

        while (ancestor) {
            const computedStyle = window.getComputedStyle(ancestor);
            const overflowValues = `${computedStyle.overflow} ${computedStyle.overflowY}`;

            if (/\b(auto|overlay|scroll)\b/.test(overflowValues)) {
                return ancestor;
            }

            ancestor = ancestor.parentElement;
        }

        return footerContainer;
    }, [resolveFooterContainer]);
    const measureScrollableContent = useCallback(() => {
        const boundary = boundaryRef.current;
        const footerContainer = resolveFooterContainer();
        const scrollContainer = resolveScrollContainer();

        if (!boundary || !footerContainer || !scrollContainer) {
            setHasScrollableContent(false);
            return;
        }

        const footerHeight = footerRef.current?.offsetHeight ?? 0;
        const contentScrollHeight = scrollContainer.scrollHeight - footerHeight - boundary.offsetHeight;
        const nextHasScrollableContent = contentScrollHeight > scrollContainer.clientHeight + 1;
        const computedStyle = window.getComputedStyle(footerContainer);
        const nextInlinePadding = {
            end: Number.parseFloat(computedStyle.paddingInlineEnd || computedStyle.paddingRight) || 0,
            start: Number.parseFloat(computedStyle.paddingInlineStart || computedStyle.paddingLeft) || 0,
        };

        setHasScrollableContent((current) =>
            current === nextHasScrollableContent ? current : nextHasScrollableContent,
        );
        setInlinePadding((current) =>
            current.end === nextInlinePadding.end && current.start === nextInlinePadding.start
                ? current
                : nextInlinePadding,
        );
    }, [resolveFooterContainer, resolveScrollContainer]);
    const scheduleMeasurement = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }

        if (measurementFrameRef.current !== undefined) {
            window.cancelAnimationFrame(measurementFrameRef.current);
        }

        measurementFrameRef.current = window.requestAnimationFrame(() => {
            measurementFrameRef.current = undefined;
            measureScrollableContent();
        });
    }, [measureScrollableContent]);

    useLayoutEffect(() => {
        const scrollContainer = resolveScrollContainer();

        scheduleMeasurement();

        if (!scrollContainer) {
            return;
        }

        const footerContainer = resolveFooterContainer();

        const resizeObserver =
            typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(scheduleMeasurement);
        resizeObserver?.observe(scrollContainer);
        if (footerContainer && footerContainer !== scrollContainer) {
            resizeObserver?.observe(footerContainer);
        }
        Array.from(scrollContainer.children).forEach((child) => resizeObserver?.observe(child));

        const mutationObserver =
            typeof MutationObserver === 'undefined' ? undefined : new MutationObserver(scheduleMeasurement);
        mutationObserver?.observe(scrollContainer, { characterData: true, childList: true, subtree: true });
        window.addEventListener('resize', scheduleMeasurement);

        return () => {
            mutationObserver?.disconnect();
            resizeObserver?.disconnect();
            window.removeEventListener('resize', scheduleMeasurement);

            if (measurementFrameRef.current !== undefined) {
                window.cancelAnimationFrame(measurementFrameRef.current);
                measurementFrameRef.current = undefined;
            }
        };
    }, [resolveFooterContainer, resolveScrollContainer, scheduleMeasurement]);

    return (
        <>
            <span
                aria-hidden
                data-scroll-end-boundary='true'
                ref={boundaryRef}
                style={{ display: 'block', height: 0 }}
            />
            {hasScrollableContent ? (
                <ScrollEndFooter
                    {...footerProps}
                    ref={footerRef}
                    style={{
                        ...style,
                        marginInlineEnd: -inlinePadding.end,
                        marginInlineStart: -inlinePadding.start,
                        width: `calc(100% + ${inlinePadding.start + inlinePadding.end}px)`,
                    }}
                    onBackToTop={
                        onBackToTop ?? (() => resolveScrollContainer()?.scrollTo({ behavior: 'smooth', top: 0 }))
                    }
                />
            ) : null}
        </>
    );
};

export default OverflowAwareScrollEndFooter;
export type { OverflowAwareScrollEndFooterProps };
